import React, {useEffect, useRef, useState} from "react";
import {Box, Button, Grid, Tooltip, Stack} from "@mui/joy";
import IServiceOrder from "../../../types/service-order.type";
import {useFormik} from "formik";
import {getServiceOrdersEvaluationsByServiceOrderId, update, completeEvaluation} from "../../../services/service-order-evaluation.service";
import showNotification from "../../utils/Notify";
import * as Yup from "yup";
import IServiceOrderEvaluation from "../../../types/service-order-evaluation.type";
import {
  Checkbox,
  Chip as ChipMaterial,
  FormControlLabel, InputAdornment,
  TextField,
  Tooltip as MaterialTooltip
} from "@mui/material";
import {Experimental_CssVarsProvider as CssVarsProvider} from "@mui/material/styles/ThemeProviderWithVars";
import Divider from "@mui/joy/Divider";
import {RevisionItemCategory} from "../../../types/enumeration/RevisionItemCategory";
import IServiceOrderEvaluationItem from "../../../types/service-order-evaluation-item.type";
import {GridSaveAltIcon} from "@mui/x-data-grid";
import ManageSearchIcon from "@mui/icons-material/ManageSearch";
import {ServiceOrderEvaluationStatus} from "../../../types/enumeration/ServiceOrderEvaluationStatus";
import {ServiceOrderEvaluationStatusColors} from "../../../types/enumeration/ServiceOrderEvaluationStatusColors";
import {ServiceOrderEvaluationStatusNames} from "../../../types/enumeration/ServiceOrderEvaluationStatusNames";
import StatusHistoryModal from "../../utils/StatusHistoryModal";
import {WorkPosition} from "../../../types/enumeration/WorkPosition";
import IUser from "../../../types/user.type";
import IAllocatedResource from "../../../types/allocated-resource.type";
import {WorkPositionNames} from "../../../types/enumeration/WorkPositionNames";
import {Autocomplete} from "@mui/lab";
import IBudget from "../../../types/budget.type";
import {BudgetConcept} from "../../../types/enumeration/BudgetConcept";
import {ArrowBackIosNew} from "@mui/icons-material";
import ConfirmationModal from "../../utils/ConfirmationModal";
import IServiceOrderEvaluationChangeStatus from "../../../types/service-order-evaluation-change-status.type";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";

interface Props {
  serviceOrder: IServiceOrder;
  isActive: boolean;
  goBack: () => void,
}

const ServiceOrderEvaluationForm: React.FC<Props> = ({serviceOrder, isActive, goBack}) => {
  const [serviceOrderEvaluations, setServiceOrderEvaluations] = useState<Array<IServiceOrderEvaluation>>([]);
  const [statusHistoryModalOpen, setStatusHistoryModalOpen] = React.useState(false);
  const [confirmationModalOpen, setConfirmationModalOpen] = React.useState(false);
  const [modalTitle, setModalTitle] = React.useState("");
  const [modalMessage, setModalMessage] = React.useState("");
  const [selectedNewStatus, setSelectedNewStatus] = React.useState<ServiceOrderEvaluationStatus | null>(null);

  const handleStatusHistoryModalOpen = () => {
    setStatusHistoryModalOpen(true);
  }

  const handleStatusHistoryModalClose = () => {
    setStatusHistoryModalOpen(false);
  }

  const handleOpenChangeStatusModal = (status: ServiceOrderEvaluationStatus) => {
    setModalTitle("Completar Evaluacion de " + allocatedResource?.worker?.name + " " + allocatedResource?.worker?.lastName);
    setModalMessage("Se completara la Evaluacion de Desempeño y no se podra volver a editar.")
    setSelectedNewStatus(status);
    setConfirmationModalOpen(true);
  };

  const onConfirm = async (comments?: string) => {
    try {
      // 1. Ejecuta la validación y el submit de Formik primero
      await formik.submitForm();

      // 2. Si el submit fue exitoso, ejecuta tu lógica de confirmación
      if (selectedNewStatus) {
        let newStatus: IServiceOrderEvaluationChangeStatus = {
          status: selectedNewStatus,
          comments: comments ? comments : '',
        };
        const response = await completeEvaluation(selectedEvaluation.id, newStatus);
        showNotification({type: 'success', message: "Evaluacion completada correctamente."});
        fetchServiceOrderEvaluations();
      }
    } catch (error) {
      // El error ya lo maneja el catch de onSubmit o completeEvaluation
    } finally {
      setSelectedNewStatus(null);
      setConfirmationModalOpen(false);
    }
  };

  const handleConfirmationModalClose = () => {
    setSelectedNewStatus(null);
    setConfirmationModalOpen(false);
  }

  const initialValues: IServiceOrderEvaluation = {
    id: null,
    notes: "",
    result: 0,
    bonus: 0,
    hours: 0,
    allocatedResource: null,
    evaluationItems: [],
    status: ServiceOrderEvaluationStatus.IN_PROGRESS,
    statusHistory: [],
    serviceOrder: null,
    bonusUpTo: null,
    workPosition: null,
  }

  const onSubmit = (serviceOrderEvaluation: IServiceOrderEvaluation) => {
    if (!serviceOrderEvaluation.id) return Promise.reject();

    // Retorna la promesa para poder encadenarla
    return update(serviceOrderEvaluation.id, serviceOrderEvaluation)
      .then(() => {
        showNotification({type: 'success', message: 'Evaluacion actualizada correctamente.'});
        fetchServiceOrderEvaluations();
      })
      .catch(error => {
        showNotification({type: 'error', message: error.response.data.message});
        throw error; // Lanza el error para frenar el siguiente paso
      });
  };

  const validationSchema = Yup.object({
    //result: Yup.object().required('El Resultado es requerido'),
    // evaluationItems: Yup.array().of(
    //   Yup.object().shape({
    //     result: Yup.string()
    //       .required('El resultado es requerido')
    //       .nullable(),
    //
    //     notes: Yup.string()
    //       .max(500, 'Máximo 500 caracteres')
    //       .nullable(),
    //
    //     revisionItem: Yup.object().shape({
    //       rating: Yup.number()
    //         .required('La ponderación es requerida')
    //         .min(0, 'No puede ser menor a 0%')
    //         .max(100, 'No puede ser mayor a 100%')
    //         .nullable(),
    //     }),
    //   })
    // ),
  });

  const formik = useFormik({
    enableReinitialize: false,
    initialValues: initialValues,
    onSubmit,
    validationSchema: validationSchema,
  });

  const [selectedEvaluation, setSelectedEvaluation] = useState<IServiceOrderEvaluation>(initialValues);
  const [allocatedResource, setAllocatedResource] = useState<IAllocatedResource>();


  const TruncatedDescription: React.FC<{ text: string }> = ({ text }) => {
    const textRef = useRef<HTMLDivElement | null>(null);
    const [isOverflowing, setIsOverflowing] = useState(false);

    useEffect(() => {
      const element = textRef.current;
      if (!element) {
        return;
      }

      const updateOverflow = () => {
        setIsOverflowing(element.scrollHeight > element.clientHeight || element.scrollWidth > element.clientWidth);
      };

      updateOverflow();

      const resizeObserver = new ResizeObserver(updateOverflow);
      resizeObserver.observe(element);

      window.addEventListener("resize", updateOverflow);

      return () => {
        resizeObserver.disconnect();
        window.removeEventListener("resize", updateOverflow);
      };
    }, [text]);

    return (
      <Tooltip title={text} arrow placement="top-start" disableHoverListener={!isOverflowing}>
        <Box
          ref={textRef}
          sx={{
            minWidth: 0,
            display: "-webkit-box",
            WebkitBoxOrient: "vertical",
            WebkitLineClamp: 2,
            overflow: "hidden",
            textOverflow: "ellipsis",
            wordBreak: "break-word",
            lineHeight: 1.35,
            maxWidth: "100%",
          }}
        >
          {text}
        </Box>
      </Tooltip>
    );
  };

  const calculateItemResult = (rating: number, weighing: number) => {
    return (((rating * weighing) / 100)).toFixed(2);
  }

  const calculateTotalResult = () => {
    const totalResult = formik.values.evaluationItems.reduce((sum, item) => {
      if (Boolean(item.notApply)) {
        return sum;
      }

      const itemResult = Number(item.result) || 0;
      return sum + itemResult;
    }, 0);

    // @ts-ignore
    const totalBonus = (formik.values.bonusUpTo * totalResult) / 100;

    formik.setFieldValue("result", Number(totalResult.toFixed(2)));
    formik.setFieldValue("bonus", Number(totalBonus.toFixed(2)));
  };

  const renderEvaluationSection = (
    title: string,
    category: RevisionItemCategory,
    allItems: IServiceOrderEvaluationItem[]
  ) => {
    const filteredItems = allItems
      .map((item, index) => ({ item, index }))
      .filter(({ item }) => item.revisionItem.category === category);

    return (
      <>
        <Divider sx={{ my: 2 }}>{title}</Divider>
        {filteredItems.map(({ item, index }) => {
          const itemError = formik.errors.evaluationItems?.[index] as any;
          const itemTouched = formik.touched.evaluationItems?.[index] as any;

          return (
            <Box
              key={item.id || index}
              sx={{
                display: "grid",
                gridTemplateColumns: "1fr 110px 120px 90px 110px 1.8fr",
                gap: 1,
                alignItems: "center",
                px: 1,
                py: 1,
              }}
            >
              <TruncatedDescription text={item.revisionItem.description} />
              <CssVarsProvider>
                <TextField
                  label="Clasificacion"
                  type="number"
                  variant="filled"
                  value={item.rating ?? ""}
                  error={Boolean(itemTouched?.revisionItem?.rating && itemError?.revisionItem?.rating)}
                  helperText={itemTouched?.revisionItem?.rating && itemError?.revisionItem?.rating}
                  disabled={Boolean(item.notApply)}
                  onChange={(e) => {
                    formik.setFieldValue(`evaluationItems[${index}].rating`, e.target.value === "" ? null : Number(e.target.value));
                    formik.setFieldValue(`evaluationItems[${index}].result`, calculateItemResult(Number(e.target.value), item.weighing));
                  }}
                  slotProps={{
                    inputLabel: { shrink: true },
                    htmlInput: { readOnly: formik.values.status === ServiceOrderEvaluationStatus.COMPLETED, style: { textAlign: "center" }},
                  }}
                />
              </CssVarsProvider>
              <CssVarsProvider>
                <TextField
                  label="Ponderación"
                  value={item.weighing ?? 0}
                  disabled={Boolean(item.notApply)}
                  onChange={(e) => {
                    formik.setFieldValue(`evaluationItems[${index}].weighing`, e.target.value === "" ? null : Number(e.target.value));
                    formik.setFieldValue(`evaluationItems[${index}].result`, calculateItemResult(item.rating, Number(e.target.value)));
                  }}
                  variant="filled"
                  slotProps={{
                    inputLabel: { shrink: true },
                    htmlInput: { readOnly: formik.values.status === ServiceOrderEvaluationStatus.COMPLETED, style: { textAlign: "right" } },
                    input: {
                      endAdornment: <InputAdornment sx={{mt: 2, mr: 3}} position="end">%</InputAdornment>,
                    }
                  }}
                />
              </CssVarsProvider>

              <Box sx={{ display: "flex", justifyContent: "center" }}>
                <CssVarsProvider>
                  <MaterialTooltip title={"No Aplica"} arrow placement="right-end">
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={Boolean(item.notApply)}
                          disabled={formik.values.status === ServiceOrderEvaluationStatus.COMPLETED}
                          onChange={(e) => {
                            const isNoAplica = e.target.checked;
                            formik.setFieldValue(`evaluationItems[${index}].notApply`, isNoAplica);
                            if (isNoAplica) {
                              formik.setFieldValue(`evaluationItems[${index}].result`, 0);
                              formik.setFieldValue(`evaluationItems[${index}].rating`, 0);
                            }
                          }}
                        />
                      }
                      label="N/A"
                    />
                  </MaterialTooltip>
                </CssVarsProvider>
              </Box>

              <CssVarsProvider>
                <TextField
                  label="Resultado"
                  variant="filled"
                  value={item.result ?? ""}
                  disabled={Boolean(item.notApply)}
                  onChange={(e) =>
                    formik.setFieldValue(`evaluationItems[${index}].result`, e.target.value)
                  }
                  slotProps={{
                    inputLabel: { shrink: true },
                    htmlInput: { readOnly: true, style: { textAlign: "center" } },
                  }}
                />
              </CssVarsProvider>

              {/* Observaciones */}
              <CssVarsProvider>
                <TextField
                  label="Observaciones"
                  variant="filled"
                  multiline
                  minRows={1}
                  value={item.notes ?? ""}
                  onChange={(e) =>
                    formik.setFieldValue(`evaluationItems[${index}].notes`, e.target.value)
                  }
                  slotProps={{
                    inputLabel: { shrink: true },
                    htmlInput: { readOnly: formik.values.status === ServiceOrderEvaluationStatus.COMPLETED },
                  }}
                />
              </CssVarsProvider>
            </Box>
          );
        })}
      </>
    );
  };

  const renderEvaluationFooter = () => (
    <Box
      sx={{
        mt: 2,
        px: 1,
        py: 1,
        display: "grid",
        gridTemplateColumns: "1fr 110px 120px 90px 110px 1.8fr",
        alignItems: "center",
        columnGap: 1,
        borderTop: "1px solid #d9d9d9",
        borderBottom: "1px solid #d9d9d9",
        backgroundColor: "#fafafa",
      }}
    >
      <Box sx={{ gridColumn: "1 / span 3" }} />

      <Box sx={{ gridColumn: 5, display: "flex", flexDirection: "column", alignItems: "center", gap: 0.5, minWidth: 0 }}>
        <CssVarsProvider>
          <TextField
            label="Resultado Total"
            value={formik.values.result}
            variant="filled"
            sx={{ width: 110 }}
            slotProps={{
              htmlInput: { readOnly: true, style: { textAlign: "center" } },
            }}
          />
        </CssVarsProvider>
      </Box>

      <Box sx={{ gridColumn: 6, display: "flex", alignItems: "flex-start", justifyContent: "flex-end", gap: 2 }}>
        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 0.5, whiteSpace: "nowrap" }}>
          <CssVarsProvider>
            <TextField
              id="hours"
              label="Horas Acreditadas"
              name="hours"
              value={formik.values.hours}
              type="number"
              variant="filled"
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              sx={{ width: 120 }}
              slotProps={{
                htmlInput: { readOnly: formik.values.status === ServiceOrderEvaluationStatus.COMPLETED, style: { textAlign: "center" } },
              }}
            />
          </CssVarsProvider>
        </Box>

        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 0.5, whiteSpace: "nowrap" }}>
          <CssVarsProvider>
            <TextField
              label="Bono Completado"
              value={formik.values.bonus}
              type="number"
              variant="filled"
              sx={{ width: 120 }}
              slotProps={{
                htmlInput: { readOnly: true, style: { textAlign: "center" } },
              }}
            />
          </CssVarsProvider>
        </Box>
      </Box>
    </Box>
  );

  const fetchServiceOrderEvaluations = async () => {
    getServiceOrdersEvaluationsByServiceOrderId(serviceOrder.id).then( (response) => {
      const evaluations = response.data;
      if (!selectedEvaluation.id) {
        setSelectedEvaluation(evaluations[0]);
        setAllocatedResource(serviceOrder.allocatedResources.find((resource: IAllocatedResource )=>
          resource.worker?.id === evaluations[0].allocatedResource.id));
      } else {
        setSelectedEvaluation(evaluations.find((evaluation: IServiceOrderEvaluation) =>
          evaluation.id === selectedEvaluation.id));
      }
      setServiceOrderEvaluations(evaluations);
    })
  }

  useEffect(() => {
    if (selectedEvaluation) {
      formik.setValues(selectedEvaluation);
      const resource = serviceOrder.allocatedResources.find((resource: IAllocatedResource) =>
        resource.worker?.id === selectedEvaluation?.allocatedResource?.id);
      const filteredBudgets = serviceOrder.budgets.filter((budget: IBudget) => {
        const isBonus = budget.concept === BudgetConcept.BONUS;
        const hasResource = budget.resources.some((u: IUser) => u.id === resource?.worker?.id);
        return isBonus && hasResource;
      });
      setAllocatedResource(resource);
    }
  }, [selectedEvaluation]);

  useEffect(() => {
    calculateTotalResult();
  }, [
    JSON.stringify(
      formik.values.evaluationItems?.map(item => ({
        result: item.result,
        weighing: item.weighing,
        notApply: item.notApply
      }))
    )
  ]);

  useEffect(() => {
    if (isActive && serviceOrder?.id) {
      fetchServiceOrderEvaluations();
    }
  }, [isActive, serviceOrder?.id]);

  return (
    <form className="form" onSubmit={formik.handleSubmit}>

      <StatusHistoryModal
        open={statusHistoryModalOpen}
        title={"Historial de la Evaluacion de Desempeño de " + selectedEvaluation?.allocatedResource?.name + " " + selectedEvaluation?.allocatedResource?.lastName}
        history={selectedEvaluation?.statusHistory}
        onCancel={handleStatusHistoryModalClose}
        historyType={"serviceOrderEvaluation"}
      />

      <ConfirmationModal
        open={confirmationModalOpen}
        onConfirm={(comments?: string) => { onConfirm(comments)}}
        onCancel={handleConfirmationModalClose}
        title={modalTitle}
        message={modalMessage}
        isDeleteAction={false}
        enableComments={true}
      />

      <Grid
        container
        rowSpacing={2}
        columnSpacing={2}
        marginBlock={1}
        marginInline={0}
        sx={{ width: '100%' }}
      >
        <Grid xl={9} md={9} sm={9} xs={9}>
          <CssVarsProvider>
            <Autocomplete
              id="Recursos a Evalua"
              value={selectedEvaluation}
              getOptionLabel={(evaluation) =>
                evaluation
                  ? `${evaluation.allocatedResource?.name || ''} ${evaluation.allocatedResource?.lastName || ''}`
                  : ''
              }
              options={serviceOrderEvaluations}
              isOptionEqualToValue={(option, value) => option?.id === value?.id}
              onChange={(e, value) => {
                setSelectedEvaluation(value);
              }}
              fullWidth
              disableClearable
              renderInput={(params) => (
                <TextField
                  {...params}
                  variant="filled"
                  name="resource"
                  label="Recurso"
                  slotProps={{
                    inputLabel: { shrink: true },
                    htmlInput: {
                      ...params.inputProps,
                      readOnly: true,
                      style: { cursor: 'pointer' }
                    }
                  }}
                />
              )}
            />
          </CssVarsProvider>
        </Grid>

        <Grid xl={3} md={3} sm={3} xs={3}>
          <CssVarsProvider>
            <ChipMaterial
              color={ServiceOrderEvaluationStatusColors[ServiceOrderEvaluationStatus[selectedEvaluation?.status as keyof typeof ServiceOrderEvaluationStatus]]}
              label={ServiceOrderEvaluationStatusNames[ServiceOrderEvaluationStatus[selectedEvaluation?.status as keyof typeof ServiceOrderEvaluationStatus]]}
              sx={{width: "100%", height: 55, fontWeight: 'bold', fontSize: 18}}
              icon={<ManageSearchIcon fontSize="medium" sx={{marginTop: -0.5}} />}
              clickable={true}
              onClick={() => handleStatusHistoryModalOpen()}
            />
          </CssVarsProvider>
        </Grid>

        <Grid xl={6} md={6} sm={6} xs={6}>
          <CssVarsProvider>
            <TextField
              fullWidth
              id="notes"
              label="Observaciones"
              name="notes"
              variant="filled"
              type={"text"}
              multiline
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              value={formik.values.notes}
              rows={4}
              slotProps={{
                inputLabel: {shrink: true},
                input: {readOnly: formik.values.status === ServiceOrderEvaluationStatus.COMPLETED}
              }}
            />
          </CssVarsProvider>
        </Grid>
        <Grid xl={6} md={6} sm={6} xs={6} container rowSpacing={1.5} >
          <Grid xl={12} md={12} sm={12} xs={12}>
            <CssVarsProvider>
              <TextField
                fullWidth
                id="position"
                label="Puesto"
                name="position"
                variant="filled"
                type={"text"}
                value={WorkPositionNames[WorkPosition[allocatedResource?.workPosition as keyof typeof WorkPosition]]}
                slotProps={{
                  inputLabel: {shrink: true},
                  input: {readOnly: true}
                }}
              />
            </CssVarsProvider>
          </Grid>
          <Grid xl={12} md={12} sm={12} xs={12}>
            <CssVarsProvider>
              <TextField
                fullWidth
                id="number"
                label="Bono"
                name="number"
                variant="filled"
                type={"text"}
                value={formik.values.bonusUpTo}
                slotProps={{
                  inputLabel: {shrink: true},
                  input: {readOnly: true}
                }}
              />
            </CssVarsProvider>
          </Grid>
        </Grid>
      </Grid>

      {renderEvaluationSection(
        "Objetivos Basicos",
        RevisionItemCategory.BASIC,
        formik.values.evaluationItems
      )}
      {renderEvaluationSection(
        "Objetivos Especificos",
        RevisionItemCategory.SPECIFIC,
        formik.values.evaluationItems
      )}
      {renderEvaluationFooter()}

      <Stack direction="row" justifyContent="space-between" spacing={2} pt={1} pr={1} pl={1}>
        <Stack direction="row" justifyContent="flex-start" spacing={2}>
          {selectedEvaluation.status === ServiceOrderEvaluationStatus.IN_PROGRESS &&
              <Button
                  onClick={() => handleOpenChangeStatusModal(ServiceOrderEvaluationStatus.COMPLETED)}
                  variant="solid"
                  color="success"
                  endDecorator={<CheckCircleOutlineIcon />}>
                  Completar Evaluacion de: &nbsp;<b><u>{allocatedResource?.worker?.name + " " + allocatedResource?.worker?.lastName}</u></b>
              </Button>
          }
        </Stack>
        <Stack direction="row" justifyContent="flex-end" spacing={2}>
          <Button onClick={() => goBack()} variant="outlined" endDecorator={<ArrowBackIosNew />}>Volver</Button>
          {selectedEvaluation.status === ServiceOrderEvaluationStatus.IN_PROGRESS &&
            <Button type="submit" variant="solid" endDecorator={<GridSaveAltIcon />}>Guardar</Button>
          }
        </Stack>
      </Stack>
    </form>
  );
};

export default ServiceOrderEvaluationForm;
